import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Navigation, Clock, Building2, Globe, ArrowRight, Share2 } from 'lucide-react';

// --- SPOTLIGHT CARD COMPONENT ---
// Yeh component cursor ko track karta hai aur border/background ko glow deta hai
const SpotlightCard = ({ children, className = "" }) => {
  const divRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);

  const handleMouseMove = (e) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseEnter = () => setOpacity(1);
  const handleMouseLeave = () => setOpacity(0);

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative overflow-hidden bg-white/40 backdrop-blur-md border border-white/20 shadow-xl ${className}`}
    >
      {/* Spotlight Gradient Overlay */}
      <div
        className="pointer-events-none absolute -inset-px transition duration-300"
        style={{
          opacity,
          background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, rgba(42,56,144,0.1), transparent 40%)`,
        }}
      />
      {/* Spotlight Border Overlay */}
      <div
        className="pointer-events-none absolute -inset-px transition duration-300"
        style={{
          opacity,
          background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, rgba(42,56,144,0.4), transparent 40%)`,
          maskImage: 'linear-gradient(black, black) content-box, linear-gradient(black, black)',
          WebkitMaskImage: 'linear-gradient(black, black) content-box, linear-gradient(black, black)',
          maskComposite: 'exclude',
          WebkitMaskComposite: 'xor',
        }}
      />
      {/* Content */}
      <div className="relative h-full">{children}</div>
    </div>
  );
};

const LocationPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { staggerChildren: 0.1, duration: 0.6 } 
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <div className="min-h-screen bg-[#f1f5f9] font-sans text-slate-800 relative overflow-x-hidden selection:bg-[#2a3890] selection:text-white">
      
      {/* --- NOISE TEXTURE OVERLAY (Realism Booster) --- */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none z-50 mix-blend-overlay" 
           style={{ backgroundImage: `url("https://www.transparenttextures.com/patterns/stardust.png")` }}>
      </div>

      {/* --- HERO SECTION WITH BLOCK PATTERN --- */}
      <div className="relative h-[45vh] min-h-[350px] flex items-center justify-center overflow-hidden bg-[#2a3890]">
        
        {/* Block/Stone Pattern Overlay */}
        <div className="absolute inset-0 opacity-30" 
             style={{ backgroundImage: `url("https://www.transparenttextures.com/patterns/cubes.png")` }}>
        </div>
        
        {/* Gradient Overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#2a3890]/30 to-[#2a3890]/100"></div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto mt-8">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight tracking-tight"
          >
            Our Headquarter
          </motion.h1>
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-10 py-12 -mt-24 relative z-20 max-w-7xl">
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-12 gap-6"
        >
          
          {/* --- LEFT SIDE: SPOTLIGHT INFO CARDS (Span 4) --- */}
          <div className="lg:col-span-4 space-y-5">
            {/* Address Card */}
            <motion.div variants={itemVariants}>
                <SpotlightCard className="p-6 md:p-8 group h-full bg-white/80">
                    <div className="w-12 h-12 bg-blue-50 flex items-center justify-center mb-5 text-[#2a3890] shadow-inner group-hover:scale-110 transition-transform duration-500"
                    style={{ borderRadius: '35%' }}
                    >   
                        <MapPin size={24} />
                    </div>
                    
                    <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                        Main Office
                    </h3>
                    <p className="text-slate-600 leading-relaxed mb-6 font-light text-sm md:text-base">
                        2231 Crystal Drive,<br />
                        Arlington, VA 22202,<br />
                        USA
                    </p>
                    
                    <div className="flex gap-3">
                        <a 
                            href="https://maps.google.com/maps?q=2231+Crystal+Drive+1000,+Arlington,+VA+22202" 
                            target="_blank" 
                            rel="noreferrer"
                            className="flex-1 inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-xl bg-[#2a3890] text-white font-semibold text-sm hover:bg-[#1e2a6f] transition-all shadow-lg shadow-blue-900/20 active:scale-95"
                        >
                            Navigate <Navigation size={14} />
                        </a>
                        <button className="p-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-[#2a3890] hover:border-[#2a3890] transition-colors">
                            <Share2 size={18} />
                        </button>
                    </div>
                </SpotlightCard>
            </motion.div>

            {/* Phone Card */}
            <motion.div variants={itemVariants}>
                <SpotlightCard className="p-4 md:p-8 group bg-white/80">
                    <div className="w-12 h-12 bg-green-50 rounded-2xl flex items-center justify-center mb-5 text-green-600 shadow-inner group-hover:scale-110 transition-transform duration-500">
                        <Phone size={24} />
                    </div>
                    
                    <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-1">Direct Line</h3>
                    <p className="text-slate-500 text-xs md:text-sm mb-4 font-medium tracking-wide uppercase">Mon-Fri, 9am - 6pm EST</p>
                    
                    <a 
                        href="tel:+12024454459 " 
                        className="text-2xl md:text-3xl font-bold text-slate-800 hover:text-green-600 transition-colors flex items-center gap-2 tracking-tight"
                    >
                        +1 (202) 445 4459 
                    </a>
                </SpotlightCard>
            </motion.div>

             {/* Business Hours Card (Dark Theme) */}
             <motion.div variants={itemVariants}>
                <SpotlightCard className="p-6 md:p-8 bg-[#312e2e] text-white border-slate-700">
                    <div className="absolute top-0 right-0 p-3 opacity-20">
                        <Clock size={80} />
                    </div>
                    
                    <div className="relative z-10">
                        <h3 className="text-base md:text-lg font-bold tracking-widest uppercase mb-4 flex items-center gap-2 text-blue-400">
                            <Clock size={16} /> Business Hours
                        </h3>
                        
                        <div className="space-y-3 text-xs md:text-sm font-light">
                            <div className="flex justify-between items-center border-b border-white/10 pb-2">
                                <span className="text-slate-300">Weekdays</span>
                                <span className="font-medium text-white">8:00 am - 6:00 pm</span>
                            </div>
                            <div className="flex justify-between items-center border-b border-white/10 pb-2">
                                <span className="text-slate-300">Saturday</span>
                                <span className="font-medium text-white">9:00 am - 4:00 pm</span>
                            </div>
                            <div className="flex justify-between items-center text-slate-400">
                                <span>Sunday</span>
                                <span className="text-blue-300 bg-blue-500/30 px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider">By Appointment Only</span>
                            </div>
                        </div>
                    </div>
                </SpotlightCard>
            </motion.div>

          </div>

          {/* --- RIGHT SIDE: MAP (Span 8) --- */}
          <motion.div 
            variants={itemVariants}
            className="lg:col-span-8 h-[500px] lg:h-auto min-h-[400px] relative"
          >
            {/* Outer Glow Effect for Map */}
            <div className="absolute -inset-1 bg-gradient-to-tr from-[#2a3890] to-blue-400 rounded-[2.5rem] blur opacity-20"></div>
            
            <div className="relative w-full h-full overflow-hidden shadow-2xl border border-white/50 z-10 bg-slate-200 group">
                
                {/* Custom Pulsing Marker Overlay */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
                    <div className="relative">
                        <div className="absolute -inset-4 bg-[#2a3890] rounded-full animate-ping opacity-20"></div>
                        <div className="absolute -inset-8 bg-[#2a3890] rounded-full animate-ping delay-75 opacity-10"></div>
                        <div className="bg-[#2a3890] text-white p-2.5 rounded-full shadow-2xl border-4 border-white transform hover:scale-110 transition-transform duration-300">
                            {/* <Building2 size={20} /> */}
                        </div>
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 bg-white px-3 py-1.5 rounded-xl shadow-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-2 group-hover:translate-y-0">
                            <p className="text-[10px] font-bold text-[#2a3890] uppercase">We are here</p>
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-white rotate-45"></div>
                        </div>
                    </div>
                </div>

                {/* Google Maps Embed */}
                <iframe 
                    title="TM International Location"
                    width="100%" 
                    height="100%" 
                    frameBorder="0" 
                    style={{ border: 0, minHeight: '100%' }}
                    src="https://maps.google.com/maps?q=2231%20Crystal%20Drive%201000%2C%20Arlington%2C%20VA%2022202&t=m&z=15&ie=UTF8&iwloc=&output=embed"
                    allowFullScreen
                    className="relative z-10 grayscale-[0.8] contrast-[1.05] group-hover:grayscale-0 transition-all duration-1000 ease-in-out"
                ></iframe>
                
                {/* Floating "Get Directions" FAB */}
                {/* <a 
                    href="https://maps.google.com/maps?q=2231+Crystal+Drive+1000,+Arlington,+VA+22202" 
                    target="_blank" 
                    rel="noreferrer"
                    className="absolute bottom-6 right-6 z-30 bg-white text-[#2a3890] px-5 py-3 rounded-full font-bold shadow-2xl hover:bg-[#2a3890] hover:text-white transition-all duration-300 flex items-center gap-2 group/btn text-sm"
                >
                    Open in Maps <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                </a> */}

            </div>
          </motion.div>

        </motion.div>
      </div>
      
    </div>
  );
};

export default LocationPage;